import { Bot, UserRound } from 'lucide-react'
import FdeSelect from '@/frontend/features/Fde/FdeSelect'
import ProjectTaskParticipants from '@/frontend/features/Fde/ProjectTaskParticipants'
import { taskDate, taskStatusTone } from '@/frontend/features/Fde/projectTaskPresentation'
import type { ProjectTaskFormData, ScalarTaskField } from '@/frontend/features/Fde/projectTaskForm'
import type { FdeCopy, ProjectTask, ProjectTaskAssignee } from '@/frontend/features/Fde/types'

interface Props {
  children?: React.ReactNode
  data: ProjectTaskFormData
  task: ProjectTask | null
  options: ProjectTaskAssignee[]
  editing: boolean
  canManage: boolean
  disabled: boolean
  copy: FdeCopy
  onField: (field: ScalarTaskField, value: string) => void
  onQuickField: (field: 'status' | 'priority', value: string) => void
  onParticipants: (values: string[]) => void
  onNotify: (values: string[]) => void
}

export default function ProjectTaskProperties({
  data,
  task,
  options,
  editing,
  canManage,
  disabled,
  copy,
  onField,
  onQuickField,
  onParticipants,
  onNotify,
  children,
}: Props) {
  const status = editing ? (data.status as ProjectTask['status']) : (task?.status ?? 'todo')
  const priority = editing ? (data.priority as ProjectTask['priority']) : (task?.priority ?? 'normal')
  const change = editing ? onField : onQuickField
  return (
    <aside
      data-testid="project-task-participants"
      className="border-t border-border/60 bg-muted/10 px-5 py-7 lg:overflow-y-auto lg:border-l lg:border-t-0"
    >
      <h2 className="mb-5 text-xs font-semibold">{copy.management.task_properties}</h2>
      <dl className="space-y-3 text-xs">
        {children}
        <div className="grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-2">
          <dt className="text-muted-foreground">{copy.management.task_status}</dt>
          <dd>
            {canManage ? (
              <FdeSelect
                ariaLabel={copy.management.task_status}
                value={status}
                disabled={disabled}
                onValueChange={(value) => change('status', value)}
                options={Object.entries(copy.management.statuses).map(([value, label]) => ({ value, label }))}
                size="sm"
                className={`border-0 px-2 text-xs shadow-none hover:bg-muted ${taskStatusTone[status]}`}
              />
            ) : (
              <span className={`inline-flex rounded-md px-2 py-1.5 font-medium ${taskStatusTone[status]}`}>
                {copy.management.statuses[status]}
              </span>
            )}
          </dd>
        </div>
        <div className="grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-2">
          <dt className="text-muted-foreground">{copy.management.task_priority}</dt>
          <dd>
            {canManage ? (
              <FdeSelect
                ariaLabel={copy.management.task_priority}
                value={priority}
                disabled={disabled}
                onValueChange={(value) => change('priority', value)}
                options={Object.entries(copy.management.priorities).map(([value, label]) => ({
                  value,
                  label,
                }))}
                size="sm"
                className="border-0 px-2 text-xs shadow-none hover:bg-muted"
              />
            ) : (
              <span className="px-2 font-medium">{copy.management.priorities[priority]}</span>
            )}
          </dd>
        </div>
        <div className="grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-2">
          <dt className="text-muted-foreground">{copy.management.task_start_on}</dt>
          <dd>
            {editing ? (
              <input
                aria-label={copy.management.task_start_on}
                type="date"
                value={data.start_on}
                max={data.due_on || undefined}
                disabled={disabled}
                onChange={(event) => onField('start_on', event.target.value)}
                className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
              />
            ) : (
              <span className="block px-2 py-1.5 font-medium">
                {task?.start_on ? taskDate(task.start_on) : copy.management.no_start_date}
              </span>
            )}
          </dd>
        </div>
        <div className="grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-2">
          <dt className="text-muted-foreground">{copy.management.task_due_on}</dt>
          <dd>
            {editing ? (
              <input
                aria-label={copy.management.task_due_on}
                type="date"
                value={data.due_on}
                min={data.start_on || undefined}
                disabled={disabled}
                onChange={(event) => onField('due_on', event.target.value)}
                className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
              />
            ) : (
              <span className="block px-2 py-1.5 font-medium">
                {task?.due_on ? taskDate(task.due_on) : copy.management.no_due_date}
              </span>
            )}
          </dd>
        </div>
      </dl>
      <section className="mt-6 border-t border-border/60 pt-5">
        <h3 className="mb-3 text-xs font-medium text-muted-foreground">
          {copy.management.task_responsibles}
        </h3>
        {editing ? (
          <ProjectTaskParticipants
            options={options}
            values={data.assignee_refs}
            savedValues={task?.assignees.map((person) => person.value) ?? []}
            notificationValues={data.notify_agent_refs}
            backlog={status === 'backlog'}
            disabled={disabled}
            copy={copy}
            onChange={onParticipants}
            onNotificationChange={onNotify}
          />
        ) : (
          <div className="space-y-3">
            {task?.assignees.length ? (
              task.assignees.map((person) => {
                const Icon = person.type === 'agent' ? Bot : UserRound
                return (
                  <div key={person.value} className="flex items-center gap-2.5">
                    <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground">
                      {person.avatar_url ? (
                        <img src={person.avatar_url} alt="" className="size-full object-cover" />
                      ) : (
                        <Icon className="size-3.5" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium">{person.name}</p>
                      <p className="truncate text-[10px] text-muted-foreground">
                        {person.type === 'agent' && person.work_state
                          ? copy.management.task_work_states[person.work_state]
                          : person.role}
                      </p>
                    </div>
                  </div>
                )
              })
            ) : (
              <p className="text-xs text-muted-foreground">{copy.management.task_unassigned}</p>
            )}
          </div>
        )}
      </section>
    </aside>
  )
}
